/**
 * `body_html`（aimg-notify側で`marked`+`xss`によりサニタイズ済みのHTML断片）から
 * プレーンテキストを取り出す。
 *
 * 告知の抜粋などは本文をプレーンテキストで表示するため、リッチな
 * 装飾は落として構わない。`dangerouslySetInnerHTML`
 * を使わずReactのテキストノード（自動エスケープ）だけで描画することで、
 * サーバー側サニタイズが万一破られてもクライアント側でHTMLが実行される
 * 経路自体が存在しない構成にする（DOMPurifyのようなDOM依存の追加サニタイズ
 * ライブラリを持ち込む必要がなく、Node/Bun環境でも決定的にテストできる）。
 */
export function stripAnnounceHtml(html: string): string {
  // 正規表現によるタグ除去は簡易実装であり、以下の既知の限界がある。
  // - 属性値内にエスケープされていない`>`が含まれる場合（例: `<a title=">">`)、
  //   タグがそこで早期に閉じたと誤認識し、以降の属性値がテキストとして
  //   漏れ出す可能性がある。
  // - サロゲートペア文字はJavaScriptの正規表現エンジンが1コードユニット単位で
  //   扱うため、`[^>]`等の文字クラスがペアを分断しても構文上は問題ないが、
  //   理論上は分断されたコードユニット単体が出力に混入し得る。
  // どちらも入力は`body_html`側（aimg-notify、`marked`+`xss`でサニタイズ済み）
  // であり、`dangerouslySetInnerHTML`を使わずReactのテキストノードとして
  // 描画するため、上記の限界が実行可能なHTML/JSに繋がる経路は無い
  // （表示上テキストが多少崩れる程度。ファイル冒頭のコメント参照）。
  // そのため実行リスクが無い範囲での過剰対応（HTMLパーサー導入等）はしない。
  const withoutTags = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]*>/g, " ");
  return decodeHtmlEntities(withoutTags).replace(/\s+/g, " ").trim();
}

const MARKUP_LINE_BREAK_BOUNDARY = String.raw`(?:<\/(?:blockquote|div|h[1-6]|li|ol|p|pre|tr|ul)>|<br\s*\/?>)`;
const MARKUP_LINE_BREAK_BOUNDARY_SEQUENCE = new RegExp(
  `${MARKUP_LINE_BREAK_BOUNDARY}(?:[ \\t]*${MARKUP_LINE_BREAK_BOUNDARY})*`,
  "gi",
);

/**
 * サニタイズ済み`body_html`を安全なプレーンテキストへ変換しつつ、
 * Markdown由来の改行とブロック境界を保持する。
 */
export function formatAnnounceHtmlAsPlainText(html: string): string {
  const codeBlocks: string[] = [];
  const decodedHtml = decodeHtmlEntities(html);
  let codeBlockMarkerPrefix = "__ANNOUNCEMENT_CODE_BLOCK_";
  while (
    html.includes(codeBlockMarkerPrefix) ||
    decodedHtml.includes(codeBlockMarkerPrefix)
  ) {
    codeBlockMarkerPrefix = `_${codeBlockMarkerPrefix}`;
  }
  const withCodeBlocksProtected = html.replace(
    /(<pre\b[^>]*>)([\s\S]*?)(<\/pre>)/gi,
    (_match, openTag: string, content: string, closeTag: string) => {
      const marker = `${codeBlockMarkerPrefix}${codeBlocks.length}__`;
      codeBlocks.push(
        decodeHtmlEntities(
          content.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]*>/g, ""),
        ).replace(/\r\n?/g, "\n"),
      );
      return `${openTag}${marker}${closeTag}`;
    },
  );
  // markedがタグ間へ出力する整形用改行を、意味上の改行へ変換する前に除去する。
  // pre内部は空白だけの行にも意味があるため、整形改行の処理対象から外す。
  const withoutMarkupFormatting = withCodeBlocksProtected.replace(
    /(<pre\b[^>]*>[\s\S]*?<\/pre>)|>[ \t]*(?:\r?\n[ \t]*)+</gi,
    (_match, preBlock: string | undefined) => preBlock ?? "><",
  );
  const withLineBreaks = withoutMarkupFormatting
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<hr\s*\/?>/gi, "\n")
    .replace(
      MARKUP_LINE_BREAK_BOUNDARY_SEQUENCE,
      (boundary, offset: number, source: string) => {
        const explicitBreaks = boundary.match(/<br\s*\/?>/gi)?.length ?? 0;
        const hasStructuralClose = /<\//.test(boundary);
        let structuralBreaks = hasStructuralClose
          ? /<\/(?:ol|p|ul)>/i.test(boundary)
            ? 2
            : 1
          : 0;
        const previousCodeBlock = new RegExp(
          `${codeBlockMarkerPrefix}(\\d+)__$`,
        ).exec(source.slice(0, offset));
        if (
          structuralBreaks > 1 &&
          previousCodeBlock &&
          codeBlocks[Number(previousCodeBlock[1])]?.endsWith("\n")
        ) {
          structuralBreaks -= 1;
        }
        return "\n".repeat(structuralBreaks + explicitBreaks);
      },
    )
    .replace(/<[^>]*>/g, " ");

  return decodeHtmlEntities(withLineBreaks)
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .trim()
    .replace(
      new RegExp(`${codeBlockMarkerPrefix}(\\d+)__`, "g"),
      (_match, index: string) => codeBlocks[Number(index)] ?? "",
    );
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/** Unicodeの最大コードポイント（`String.fromCodePoint`が受理する上限）。 */
const MAX_CODE_POINT = 0x10ffff;
/** サロゲート範囲（単独では有効な文字を表さないため`String.fromCodePoint`が例外を投げる）。 */
const SURROGATE_RANGE_START = 0xd800;
const SURROGATE_RANGE_END = 0xdfff;

function isValidCodePoint(codePoint: number): boolean {
  return (
    Number.isFinite(codePoint) &&
    codePoint >= 0 &&
    codePoint <= MAX_CODE_POINT &&
    !(codePoint >= SURROGATE_RANGE_START && codePoint <= SURROGATE_RANGE_END)
  );
}

export function decodeHtmlEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === "#") {
      const codePoint =
        entity[1]?.toLowerCase() === "x"
          ? Number.parseInt(entity.slice(2), 16)
          : Number.parseInt(entity.slice(1), 10);
      // 範囲外(0x10FFFF超)・サロゲート単体はString.fromCodePointがRangeErrorを
      // 投げるため、フェイルセーフとして元のマッチ文字列をそのまま返す
      // (バナー全体のレンダークラッシュを防ぐ)。
      return isValidCodePoint(codePoint)
        ? String.fromCodePoint(codePoint)
        : match;
    }
    const decoded = NAMED_ENTITIES[entity.toLowerCase()];
    return decoded ?? match;
  });
}
